import React from 'react';
import OverWindow from '../TabList/EditOverWindow';
import WizardSectionNav from './WizardSectionNav';
import InputWithClear from '../InputComponents/InputWithClear';
import CrudTypesEnum from '../../Enums/CrudTypesEnum';
import FigmaButton from '../FigmaButton';
import ButtonColorsEnum from '../../Enums/ButtonColorsEnum';
import ButtonTypeEnum from '../../Enums/ButtonTypeEnum';
import IconPositionEnum from '../../Enums/IconPositionEnum';
import InputTypesEnum from '../../Enums/InputTypesEnum';
import ErrorMessage from '../ErrorMessage';

class GeneralEditWizzardPage extends React.Component {
  static defaultProps = {
    clickOnSave: (step, editedItem) => {},
    clickOnCancel: () => {},
    crudType: CrudTypesEnum.Create,
    editedItem: null,
    onChangeEditedItem: (newEditedItem) => {},
    step: 0,
    visitedSteps: [0],
    // Renders the same inputs, but no change handler, validityChecker or eraser runs and the body is
    // `inert`. Not `disabled: true` — that swaps in an empty PlaceHolderField and loses the value.
    readOnly: false,
    // Field body only, without OverWindow chrome / nav / footer / section header, for embedding.
    embedded: false,
    // Prefixes the generated input/label DOM ids, so two instances of the same page don't collide.
    fieldIdPrefix: '',
    // Optional `(field) => boolean`. True keeps a hidden field's row as an unpainted spacer instead of
    // dropping it, which is how the side-by-side wizard keeps its two columns aligned.
    shouldReserveHiddenFieldSpace: null,
    serverError: null, // Backend rejection of the last save, shown in the footer (see GeneralEditWizzardBase)
    onClearServerError: () => {},
    windowDescription: {
      navigateToOnCreation: null, // A base url to navigate to after creation. The id of the created item will be added to the end of the url.
      pagesInGeneralEditWizzard: [
        {
          fields: [
            {
              className: null,
              disableClearButton: false,
              disabled: false,
              EntityId: null,
              EntityTypeId: null,
              fieldName: null, //This can also be a function that returns a string. (editedItem) => {}
              hide: false, //This can also be a function that returns a boolean. (editedItem) => {}
              hideOnReadOnlySide: false, //Like `hide`, but only applied when this page is readOnly. Ignored otherwise.
              hideOnEditSide: false, //Like `hide`, but only applied when this page is NOT readOnly. Ignored otherwise.
              idFilter: null,
              idFilterArray: {}, //Some inputs will use multiple values to filter
              label: null,
              onFieldChange: (editedItem) => {}, //Can change the value of the field regardless of the user input.
              OnFieldChangeDelayMs: null, //Opt-in: delay (debounce) onFieldChange by this many ms (e.g. to avoid a backend call on every keystroke). The typed value is still applied immediately.
              roleKey: null,
              selectvalues: null, //This can be used to insert custom values into a Select field
              showFilterAt: null, //This can also be a function that returns a number. (editedItem) => {}
              type: InputTypesEnum.EmptySpace,
              validityChecker: (field, editedItem) => {}, //Check if the field value is correct
              acceptedTypes: '*', //This can be used to limit the file types that can be uploaded
            },
          ],
        },
      ],
      serviceFunction: () => {}, //The function that will be called when the user clicks on 'Finish', it is in most cases a backend call.
      titleForCreate: 'Add New Item',
      titleForEdit: 'Edit Item',
      finishButtonLabel: 'Finish',
      errorCode: '',
    },
  };

  constructor(props) {
    super(props);

    const newWindowDescription = {
      ...props.windowDescription,
      pagesInGeneralEditWizzard: props.windowDescription.pagesInGeneralEditWizzard.filter(
        (f) => f.hide === undefined || f.hide(props.editedItem) === false
      ),
    };

    this.state = {
      editedItem: props.editedItem,
      saving: false,
      windowDescription: newWindowDescription,
      formIsValid: true,
      sectionInvalid: [],
      invalidFieldCount: 0,
    };

    this.progressBarHeight = 64;
    this.onFieldChangeTimers = {};

    // Pristine snapshot for the cancel dirty-check. A JSON string, so the in-place
    // mutations changeEditedItemValue makes to nested objects of the live item can't
    // retroactively change the baseline.
    try {
      this.initialEditedItemJson = JSON.stringify(props.editedItem);
    } catch (e) {
      this.initialEditedItemJson = null;
    }

    this.clickOnSubmit = this.clickOnSubmit.bind(this);
    this.clickOnProgressBarDot = this.clickOnProgressBarDot.bind(this);
    this.changeOfEditedItem = this.changeOfEditedItem.bind(this);
    this.changeOfEditedMultipleItem = this.changeOfEditedMultipleItem.bind(this);
    this.hasUnsavedChanges = this.hasUnsavedChanges.bind(this);
    this.setBodyRef = this.setBodyRef.bind(this);
  }

  // `inert` blocks clicks and tab stops on a read-only page. Set through a ref: React 18 has no prop.
  setBodyRef(element) {
    if (element) {
      element.inert = this.props.readOnly;
    }
  }

  // True when the form differs from the item the wizard opened with — drives whether
  // closing needs the data-loss confirm. Errs toward true (confirm shows) if the
  // snapshot or comparison fails.
  hasUnsavedChanges() {
    if (this.initialEditedItemJson === null) return true;
    try {
      return JSON.stringify(this.state.editedItem) !== this.initialEditedItemJson;
    } catch (e) {
      return true;
    }
  }

  componentDidMount() {
    this.validityCheckerForTheEntireForm();
    this.computeSectionValidity(this.state.editedItem);
  }

  //#region Field visibility
  /** Evaluates one hide rule — a boolean or an `(editedItem) => boolean`. */
  evaluateHideRule(rule, editedItem) {
    return (typeof rule === 'function' ? rule(editedItem) : rule) === true;
  }

  /**
   * Hidden on the read-only or the editable side. Takes `readOnly` as an argument so a caller can ask
   * about the OTHER side than the one it renders — the side-by-side wizard needs both answers.
   */
  isFieldHiddenOnSide(field, editedItem, readOnly) {
    const sideRule = readOnly ? field.hideOnReadOnlySide : field.hideOnEditSide;
    return this.evaluateHideRule(field.hide, editedItem) || this.evaluateHideRule(sideRule, editedItem);
  }

  /**
   * Left out of this page — not validated, not counted, not rendered. `hide` always applies,
   * `hideOnReadOnlySide` / `hideOnEditSide` only on their own side, so either key is harmless elsewhere.
   */
  isFieldHidden(field, editedItem) {
    return this.isFieldHiddenOnSide(field, editedItem, this.props.readOnly);
  }
  //#endregion

  computeSectionValidity(item) {
    // A read-only page gates no save and has no nav or footer to report into.
    if (this.props.readOnly) {
      return;
    }
    const pages = this.state.windowDescription.pagesInGeneralEditWizzard;
    Promise.all(
      pages.map((page) => {
        const checks = page.fields
          .filter((f) => !this.isFieldHidden(f, item) && f.validityChecker)
          .map((f) => {
            const value = typeof f.fieldName === 'function' ? f.fieldName(item) : item[f.fieldName];
            return Promise.resolve(f.validityChecker(value, item))
              .then((err) => !!err)
              .catch(() => false);
          });
        return Promise.all(checks).then((results) => ({
          invalid: results.some(Boolean),
          count: results.filter(Boolean).length,
        }));
      })
    ).then((perSection) =>
      this.setState({
        sectionInvalid: perSection.map((s) => s.invalid),
        invalidFieldCount: perSection.reduce((sum, s) => sum + s.count, 0),
      })
    );
  }

  getSectionStatus(i) {
    if (i === this.props.step) return 'EDITING';
    if (this.state.sectionInvalid[i]) return 'INCOMPLETE';
    if (this.props.visitedSteps.includes(i)) return 'VISITED';
    return 'STUB';
  }

  componentWillUnmount() {
    Object.values(this.onFieldChangeTimers).forEach((timer) => clearTimeout(timer));
  }

  componentDidUpdate(newProps) {
    // A read-only page is a pure projection of props.editedItem, so it must adopt a replaced item
    // (e.g. an extraction result arriving while the wizard is open).
    if (this.props.readOnly && newProps.editedItem !== this.props.editedItem) {
      this.setState({ editedItem: this.props.editedItem });
    }
    if (newProps !== this.props) {
      this.setState({ saving: false, formIsValid: true });
      this.validityCheckerForTheEntireForm();
    }
  }

  //#region Validation
  validityCheckerForTheEntireForm(newEditedItem) {
    // Nothing on a read-only page can be invalid: no save to block, no error chrome to show.
    if (this.props.readOnly) {
      return;
    }
    const currentEditedItem = newEditedItem ?? this.state.editedItem;

    //If we are on the last page, we have to check all fields, not only the fields on the current page
    if (this.props.step === this.state.windowDescription.pagesInGeneralEditWizzard.length - 1) {
      for (const page of this.state.windowDescription.pagesInGeneralEditWizzard) {
        for (const field of page.fields) {
          this.validateField(field, currentEditedItem);
        }
      }
      return;
    }

    for (const field of this.state.windowDescription.pagesInGeneralEditWizzard[this.props.step].fields) {
      this.validateField(field, currentEditedItem);
    }
  }

  validateField(field, currentEditedItem) {
    if (this.isFieldHidden(field, currentEditedItem) || field.validityChecker === undefined) {
      return;
    }

    const value =
      typeof field.fieldName === 'function' ? field.fieldName(currentEditedItem) : currentEditedItem[field.fieldName];
    const validation = field.validityChecker(value, currentEditedItem);

    Promise.resolve(validation)
      .then((error) => {
        if (error) {
          //console.log(error);
          this.setState({ formIsValid: false });
        }
      })
      .catch(() => ErrorMessage.SetErrorMessage(`Error! ErrorCode: GEWPF248 - ${this.props.errorCode}`));
  }
  //#endregion

  //#region Change Event
  async changeOfEditedMultipleItem(itemData, fieldName, index) {
    if (this.props.readOnly) {
      return;
    }
    const newEdited = { ...this.state.editedItem };

    // Apply the typed/selected values and commit them SYNCHRONOUSLY (before the awaits below):
    // for a controlled <input> the value update must happen inside the change event, or React
    // resets the caret to the end on the next render (see changeOfEditedItem).
    itemData.forEach((element) =>
      this.changeEditedItemValue(newEdited, fieldName ?? element.fieldName, element.newValue, element.fieldName, index)
    );
    const editedFieldNames = itemData.map((element) => fieldName ?? element.fieldName);
    this.updateFormState(newEdited, this.getNewWindowDescriptionState(newEdited));

    try {
      const beforeRelated = { ...newEdited };
      // Sequential chain (not a for-await loop) so each related-data update settles in order.
      await itemData.reduce(async (previous, element) => {
        await previous;
        await this.updateRelatedData(newEdited, fieldName ?? element.fieldName);
      }, Promise.resolve());
      this.reconcileRelatedDataChanges(beforeRelated, newEdited, editedFieldNames);
    } catch {
      ErrorMessage.SetErrorMessage(`Error! ErrorCode: GEWPF159 - ${this.props.errorCode}`);
    }
  }

  async changeOfEditedItem(fieldName, newValue, valueFieldName, index) {
    // Read-only mode swallows every change: the inputs are the real ones, they just can't mutate.
    if (this.props.readOnly) {
      return;
    }
    const newEdited = { ...this.state.editedItem };

    this.changeEditedItemValue(newEdited, fieldName, newValue, valueFieldName, index);

    // Commit the typed value SYNCHRONOUSLY so the controlled <input> keeps its caret position.
    // Previously the setState (in updateFormState) ran only after `await updateRelatedData`,
    // deferring the value update out of the change event, which made typing in the middle of a
    // field jump the caret to the end after the first keystroke.
    this.updateFormState(newEdited, this.getNewWindowDescriptionState(newEdited));

    try {
      const beforeRelated = { ...newEdited };
      await this.updateRelatedData(newEdited, fieldName);
      this.reconcileRelatedDataChanges(beforeRelated, newEdited, [fieldName]);
    } catch {
      ErrorMessage.SetErrorMessage(`Error! ErrorCode: GEWPF158 - ${this.props.errorCode}`);
    }
  }

  // After the async related-data step (onChangeEditedItem / onFieldChange), fold only the fields
  // it actually changed back into the LATEST committed state — never the field(s) the user just
  // edited — so a newer keystroke is not clobbered by a late related-data update, and re-derive
  // page visibility/validity from the merged item. No-op when nothing dependent changed.
  reconcileRelatedDataChanges(before, after, editedFieldNames) {
    const changedKeys = Object.keys(after).filter(
      (key) => !editedFieldNames.includes(key) && after[key] !== before[key]
    );
    if (changedKeys.length === 0) {
      return;
    }
    const mergedEdited = { ...this.state.editedItem };
    changedKeys.forEach((key) => (mergedEdited[key] = after[key]));
    this.updateFormState(mergedEdited, this.getNewWindowDescriptionState(mergedEdited));
  }

  changeEditedItemValue(editedItem, fieldName, newValue, valueFieldName, index) {
    if (index === undefined) {
      editedItem[fieldName] = newValue;
    } else {
      const field = editedItem[fieldName];
      field[index][valueFieldName] = newValue;
      editedItem[fieldName] = field;
    }
  }

  async updateRelatedData(editedItem, fieldName) {
    // Await so an async onChangeEditedItem (e.g. a dependent-field fetch) mutates editedItem
    // before it is committed to state. A synchronous handler resolves immediately.
    if (this.props.onChangeEditedItem) await this.props.onChangeEditedItem(editedItem);

    const field = this.state.windowDescription.pagesInGeneralEditWizzard[this.props.step].fields.find(
      (f) => f.fieldName === fieldName
    );

    if (!field?.onFieldChange) {
      return;
    }

    // Opt-in: defer the (often backend-hitting) handler so it doesn't fire on
    // every keystroke. The typed value is already on editedItem, so the caller's
    // updateFormState still renders it immediately; only the handler is delayed.
    if (field.OnFieldChangeDelayMs) {
      this.delayOnFieldChange(field, fieldName);
      return;
    }

    await this.applyOnFieldChange(field, editedItem, fieldName);
  }

  async applyOnFieldChange(field, editedItem, fieldName) {
    const onFieldChangeResult = await Promise.resolve(field.onFieldChange(editedItem));
    if (onFieldChangeResult) {
      editedItem[fieldName] = onFieldChangeResult;
    }
  }

  delayOnFieldChange(field, fieldName) {
    clearTimeout(this.onFieldChangeTimers[fieldName]);
    this.onFieldChangeTimers[fieldName] = setTimeout(() => {
      // Re-read the latest edited item so the handler sees the final typed value.
      const latestEdited = { ...this.state.editedItem };

      this.applyOnFieldChange(field, latestEdited, fieldName)
        .then(() => {
          const newWindowDescription = this.getNewWindowDescriptionState(latestEdited);
          this.updateFormState(latestEdited, newWindowDescription);
        })
        .catch(() => ErrorMessage.SetErrorMessage(`Error! ErrorCode: GEWPF191 - ${this.props.errorCode}`));
    }, field.OnFieldChangeDelayMs);
  }

  getNewWindowDescriptionState(editedItem) {
    return {
      ...this.state.windowDescription,
      pagesInGeneralEditWizzard: this.props.windowDescription.pagesInGeneralEditWizzard.filter(
        (f) => f.hide === undefined || f.hide(editedItem) === false
      ),
    };
  }

  updateFormState(editedItem, newWindowDescription) {
    if (this.state.editedItem != editedItem) {
      this.setState({ formIsValid: true });
      this.validityCheckerForTheEntireForm(editedItem);
      // The user is editing again — the last rejection no longer describes the form.
      if (this.props.serverError) this.props.onClearServerError();
    }

    this.setState({
      editedItem: { ...editedItem },
      windowDescription: newWindowDescription,
    });

    this.computeSectionValidity(editedItem);
  }
  //#endregion

  // Prefixed so a second instance of the same page emits its own DOM ids.
  inputFieldName(fieldName) {
    return `${this.props.fieldIdPrefix}${fieldName}`;
  }

  // A read-only page runs no validation and offers no eraser.
  inputValidityChecker(validityChecker) {
    return this.props.readOnly ? null : validityChecker;
  }

  // Extension point for a per-row control after the input (the side-by-side copy-from-the-left button).
  // Not offered on the multi-row (`valueField`) branch: that field is a list, not one value.
  renderFieldRowAddon(field) {
    return null;
  }

  // Overridable so a subclass can answer for its own page instead of being handed a predicate.
  reservesSpaceForHiddenField(field) {
    return this.props.shouldReserveHiddenFieldSpace ? this.props.shouldReserveHiddenFieldSpace(field) === true : false;
  }

  renderInputField(field) {
    // A hidden field is dropped, unless space is reserved for it: then the real cell is laid out (the only
    // way to match a neighbouring column's height) but painted away, untabbable and unhittable, by
    // `.WizardFieldRowSpacer`. Either way it stays out of validation and the field counts.
    const hidden = this.isFieldHidden(field, this.state.editedItem);
    if (hidden && !this.reservesSpaceForHiddenField(field)) {
      return;
    }
    const cellClassName = `${field.className ? field.className : 'col-12'}${hidden ? ' WizardFieldRowSpacer' : ''}`;
    let calculatedValue = '';

    if (typeof field.fieldName === 'function') {
      calculatedValue = field.fieldName(this.state.editedItem);
    } else {
      calculatedValue =
        this.state.editedItem[field.fieldName] !== undefined ? this.state.editedItem[field.fieldName] : '';
    }

    const multipleIdFilter = {};
    if (field.idFilterArray) {
      for (const fieldName of field.idFilterArray) {
        if (typeof fieldName === 'string') {
          multipleIdFilter[fieldName] = this.state.editedItem[fieldName];
        } else {
          multipleIdFilter[fieldName.filterName] = fieldName.value
            ? fieldName.value
            : this.state.editedItem[fieldName.valueName];
        }
      }
    }

    const inputType = typeof field.type === 'function' ? field.type(this.state.editedItem) : field.type;
    //console.log('inputtype:: ' + inputType);

    const filterAt =
      typeof field.showFilterAt === 'function' ? field.showFilterAt(this.state.editedItem) : field.showFilterAt;

    const selectValues =
      typeof field.selectvalues === 'function' ? field.selectvalues(this.state.editedItem) : field.selectvalues;

    const info = typeof field.info === 'function' ? field.info(this.state.editedItem) : field.info;

    if (Array.isArray(calculatedValue) && field.valueField !== undefined) {
      return calculatedValue.map((value, index) => {
        const disabled = typeof field.disabled === 'function' ? (item) => field.disabled(item, index) : field.disabled;

        return (
          <div
            className={cellClassName}
            aria-hidden={hidden ? 'true' : undefined}
            key={`divk${field.fieldName}-${field.valueField}-${index}`}
          >
            <InputWithClear
              fieldName={this.inputFieldName(field.valueField)}
              labelText={value[field.labelField] === '' ? field.fieldName : value[field.labelField]}
              tooltipText={field.tooltipText}
              onChange={(valueFieldName, newValue) =>
                this.changeOfEditedItem(field.fieldName, newValue, valueFieldName, index)
              }
              type={inputType}
              validityChecker={this.inputValidityChecker((e, item) => field.validityChecker(e, item, index))}
              selectValues={selectValues}
              disabled={disabled}
              isInOneRow={true}
              EntityTypeId={field.EntityTypeId}
              EntityId={this.state.editedItem[field.EntityId]}
              margin='m-0'
              value={value[field.valueField]}
              idFilter={this.state.editedItem[field.idFilter]}
              disableClearButton={this.props.readOnly || field.disableClearButton}
              allowEmpty={field.allowEmpty}
              editedItem={this.state.editedItem}
              roleKey={field.roleKey}
              showSelectFilterAt={filterAt}
              multipleIdFilter={multipleIdFilter}
              acceptedTypes={field.acceptedTypes}
              rawFileFieldName={field.rawFileFieldName}
              textBeforeInput={field.textBeforeInput}
              numberOfRows={field.numberOfRows}
              IsCheckBoxEnabled={field.isCheckBoxEnabled}
              info={info}
              changeOfEditedMultipItem={(itemData) => this.changeOfEditedMultipleItem(itemData, field.fieldName, index)}
              getCheckBoxFieldsObject={() => this.state.editedItem[field.fieldName][index]}
            />
          </div>
        );
      });
    }
    return (
      <div
        className={cellClassName}
        aria-hidden={hidden ? 'true' : undefined}
        key={`divk${field.fieldName}`}
      >
        <InputWithClear
          fieldName={this.inputFieldName(field.fieldName)}
          labelText={field.label === '' ? field.fieldName : field.label}
          tooltipText={field.tooltipText}
          // The input reports back under the prefixed name, so use the real field name here.
          onChange={(fieldName, newValue) => this.changeOfEditedItem(field.fieldName, newValue)}
          type={inputType}
          validityChecker={this.inputValidityChecker(field.validityChecker)}
          selectValues={selectValues}
          disabled={field.disabled}
          isInOneRow={true}
          EntityTypeId={field.EntityTypeId}
          EntityId={this.state.editedItem[field.EntityId]}
          margin='m-0'
          value={calculatedValue}
          idFilter={this.state.editedItem[field.idFilter]}
          disableClearButton={this.props.readOnly || field.disableClearButton}
          allowEmpty={field.allowEmpty}
          editedItem={this.state.editedItem}
          roleKey={field.roleKey}
          showSelectFilterAt={filterAt}
          multipleIdFilter={multipleIdFilter}
          acceptedTypes={field.acceptedTypes}
          rawFileFieldName={field.rawFileFieldName}
          textBeforeInput={field.textBeforeInput}
          numberOfRows={field.numberOfRows}
          maxLength={field.maxLength}
          placeholder={field.placeholder}
          IsCheckBoxEnabled={field.isCheckBoxEnabled}
          info={info}
          buildContent={field.buildContent}
          changeOfEditedMultipItem={(itemData) => this.changeOfEditedMultipleItem(itemData)}
          getCheckBoxFieldsObject={() => this.state.editedItem}
          fileTemplateFunction={field.fileTemplateFunction}
        />
        {this.renderFieldRowAddon(field)}
      </div>
    );
  }

  clickOnSubmit(e) {
    e.preventDefault();
    this.props.clickOnSave(this.props.step, this.state.editedItem);
  }

  getNumberOfEditPages() {
    return this.state.windowDescription.pagesInGeneralEditWizzard.length;
  }

  clickOnProgressBarDot(index) {
    if (index === this.props.step) return;
    this.props.clickOnSave(index, this.state.editedItem);
  }

  renderSectionNav() {
    const pages = this.state.windowDescription.pagesInGeneralEditWizzard;
    if (pages.length === 1) return null;
    return (
      <WizardSectionNav
        sections={pages.map((p) => ({ label: p.label }))}
        currentStep={this.props.step}
        statuses={pages.map((p, i) => this.getSectionStatus(i))}
        onSelect={this.clickOnProgressBarDot}
      />
    );
  }

  renderPreviousButton() {
    if (this.getNumberOfEditPages() === 1) {
      return null;
    }
    return (
      <FigmaButton
        label='Previous'
        type={ButtonTypeEnum.NormalIconLeft}
        color={ButtonColorsEnum.Common}
        iconPosition={IconPositionEnum.Left}
        iconName='fa-solid fa-chevron-left'
        disabled={this.props.step === 0}
        onClick={() => {
          this.props.clickOnSave(this.props.step - 1, this.state.editedItem);
        }}
      />
    );
  }

  renderButton() {
    if (this.props.step !== this.state.windowDescription.pagesInGeneralEditWizzard.length - 1)
      return (
        <FigmaButton
          label='Next'
          type={ButtonTypeEnum.NormalIconRight}
          color={ButtonColorsEnum.Secondary}
          iconPosition={IconPositionEnum.Right}
          onClick={() => {
            this.setState({ saving: true });
            this.props.clickOnSave(this.props.step + 1, this.state.editedItem);
          }}
          iconName='fa-solid fa-chevron-right'
          disabled={
            this.props.step === this.state.windowDescription.pagesInGeneralEditWizzard.length - 1 ||
            !this.state.formIsValid
          }
        />
      );
    if (
      (this.props.crudType !== CrudTypesEnum.Create &&
        this.props.step >= this.state.windowDescription.pagesInGeneralEditWizzard.length - 1) ||
      !this.state.saving
    )
      return (
        <FigmaButton
          label={this.state.windowDescription.finishButtonLabel ?? 'Finish'}
          type={ButtonTypeEnum.NormalNormal}
          color={ButtonColorsEnum.Primary}
          disabled={
            (this.props.crudType === CrudTypesEnum.Create &&
              this.props.step < this.state.windowDescription.pagesInGeneralEditWizzard.length - 1) ||
            !this.state.formIsValid ||
            this.state.saving
          }
          onClick={() => {
            this.setState({ saving: true });
            this.props.clickOnSave(
              this.props.windowDescription.pagesInGeneralEditWizzard.length,
              this.state.editedItem
            );
          }}
        />
      );
    return '';
  }

  // A Delete button, shown only when editing an existing item and the description provides a deleteFunction
  // (and its optional canDelete predicate allows it for the current item).
  renderDeleteButton() {
    const windowDescription = this.state.windowDescription;
    if (this.props.crudType === CrudTypesEnum.Create || !windowDescription.deleteFunction) {
      return null;
    }
    if (windowDescription.canDelete && !windowDescription.canDelete(this.state.editedItem)) {
      return null;
    }
    return (
      <FigmaButton
        label='Delete'
        type={ButtonTypeEnum.NormalIconLeft}
        color={ButtonColorsEnum.Destruct}
        iconPosition={IconPositionEnum.Left}
        iconName='fa-solid fa-trash'
        disabled={this.state.saving}
        onClick={() => this.props.clickOnDelete(this.state.editedItem)}
      />
    );
  }

  renderFooter() {
    const pages = this.state.windowDescription.pagesInGeneralEditWizzard;
    const count = this.state.invalidFieldCount;
    return (
      <div>
        <div className='EditBoxFooter'>
          <div className='EditBoxFooterLeft'>{this.renderDeleteButton()}</div>
          <div className='EditBoxFooterInfo'>
            {this.props.serverError ? (
              <span
                className='EditBoxFooterError'
                role='alert'
              >
                <i className='fa-solid fa-circle-exclamation' /> {this.props.serverError}
              </span>
            ) : (
              count > 0 && (
                <span className='EditBoxFooterWarning'>
                  <i className='fa-solid fa-triangle-exclamation' /> {count} required field{count !== 1 ? 's' : ''} left
                </span>
              )
            )}
            {pages.length > 1 && (
              <span className='EditBoxFooterStep'>
                Step {this.props.step + 1} of {pages.length}
              </span>
            )}
          </div>
          <div className='EditBoxFooterRight'>
            {this.renderPreviousButton()}
            {this.renderButton()}
          </div>
        </div>
      </div>
    );
  }

  renderSectionHeader() {
    // The host layout owns the heading; rendering it per embedded copy would repeat it per column.
    if (this.props.embedded) return null;
    const pages = this.state.windowDescription.pagesInGeneralEditWizzard;
    if (pages.length === 1) return null;
    const page = pages[this.props.step];
    if (!page) return null;
    const num = String(this.props.step + 1).padStart(2, '0');
    // Like `hide` / `selectvalues` / `type`, may be a function of the edited item, for text that has to
    // name what is being acted on (the copy acknowledgement names the source Opportunity).
    const description =
      typeof page.description === 'function' ? page.description(this.state.editedItem) : page.description;
    return (
      <div className='WizardSectionHeader'>
        <div className='WizardSectionHeaderTop'>
          <span className='WizardSectionHeaderNum'>{num}</span>
          <span className='WizardSectionHeaderTitle'>{page.label}</span>
        </div>
        {description ? <div className='WizardSectionHeaderDesc'>{description}</div> : null}
      </div>
    );
  }

  // Extension points: the side-by-side page needs a wider window and never the compact form.
  getModalMaxWidth() {
    return 1000;
  }

  // Few-field single-page panels use the compact centered modal; a window description
  // with wide content (e.g. embedded tables) can opt out via `static compact = false;`,
  // and a short one with more fields or several pages can opt in via `static compact = true;`
  // (its pages then show as a strip across the top).
  isCompactModal() {
    const wd = this.state.windowDescription;
    const pages = wd.pagesInGeneralEditWizzard;
    if (wd.compact === true) {
      return true;
    }
    if (wd.compact === false || pages.length !== 1) {
      return false;
    }
    const visibleFieldCount = pages[0].fields.filter((f) => !this.isFieldHidden(f, this.state.editedItem)).length;
    return visibleFieldCount <= 6;
  }

  renderFormBody() {
    return (
      <div
        className='EditPanelInput'
        ref={this.setBodyRef}
      >
        {this.renderSectionHeader()}
        <div className='container-fluid'>
          <div className='row'>
            {this.state.windowDescription.pagesInGeneralEditWizzard[this.props.step].fields.map((field) =>
              this.renderInputField(field)
            )}
          </div>
        </div>
      </div>
    );
  }

  renderForm() {
    // A plain <div>, not a <form>: the host already owns one and nested forms are invalid HTML.
    if (this.props.embedded) {
      return this.renderFormBody();
    }
    return <form onSubmit={this.clickOnSubmit}>{this.renderFormBody()}</form>;
  }

  render() {
    // Embedded: just the fields; the host supplies chrome, nav and footer.
    if (this.props.embedded) {
      return this.renderForm();
    }

    const composedTitle =
      this.props.crudType === CrudTypesEnum.Create
        ? typeof this.state.windowDescription.titleForCreate === 'function'
          ? this.state.windowDescription.titleForCreate(this.state.editedItem)
          : this.state.windowDescription.titleForCreate
        : typeof this.state.windowDescription.titleForEdit === 'function'
        ? this.state.windowDescription.titleForEdit(this.state.editedItem)
        : this.state.windowDescription.titleForEdit;

    const wd = this.state.windowDescription;
    const isCreate = this.props.crudType === CrudTypesEnum.Create;
    const nameField = wd.nameField || 'Name';
    const eyebrow = wd.entityName ? `${isCreate ? 'Add' : 'Edit'} ${wd.entityName}` : null;
    const headerTitle = wd.entityName
      ? this.state.editedItem[nameField] || (isCreate ? `New ${wd.entityName}` : composedTitle)
      : composedTitle;
    const idPill =
      !isCreate && wd.idField && this.state.editedItem[wd.idField] ? this.state.editedItem[wd.idField] : null;
    const pages = wd.pagesInGeneralEditWizzard;
    const progressPercent = pages.length > 1 ? Math.round(((this.props.step + 1) / pages.length) * 100) : null;

    return (
      <OverWindow
        clickOnCancel={this.props.clickOnCancel}
        shouldConfirmCancel={this.hasUnsavedChanges}
        closeOnClickOutside={false}
        title={headerTitle}
        eyebrow={eyebrow}
        idPill={idPill}
        progressPercent={progressPercent}
        iconName={this.props.crudType === CrudTypesEnum.Create ? 'fa-solid fa-magic' : 'fa-solid fa-edit'}
        maxWidth={this.getModalMaxWidth()}
        crudType={this.props.crudType}
        compact={this.isCompactModal()}
      >
        <div className='WizardBody2Col'>
          {this.renderSectionNav()}
          {this.renderForm()}
        </div>
        {this.renderFooter()}
      </OverWindow>
    );
  }
}

export default GeneralEditWizzardPage;
